import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Brain,
  Scale,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Plus,
  ChevronRight,
  ExternalLink,
  Target,
} from "lucide-react";
import {
  LineChart as ReLine,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import Navbar from "../components/Navbar";
import DigestModal from "../components/DigestModal";
import BrokerCallsPanel from "../components/BrokerCallsPanel";
import { api } from "../lib/api";
import {
  fmtMoney,
  fmtPct,
  fmtLarge,
  fmtRatio,
  verdictStyle,
  marginColor,
  symbolFor,
  marketFromTicker,
  displayTicker,
  externalResearchLinks,
} from "../lib/format";
import { Button } from "../components/ui/button";

export default function StockDetail() {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(true);
  const [digestOpen, setDigestOpen] = useState(false);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setAiLoading(true);
      try {
        const [snapRes, histRes, recsRes] = await Promise.all([
          api.get(`/stocks/${ticker}`),
          api.get(`/stocks/${ticker}/history?period=1y`),
          api.get(`/stocks/${ticker}/recommendations`),
        ]);
        setAnalysis({ snapshot: snapRes.data, ai: null });
        setHistory(histRes.data?.data || []);
        setRecs(recsRes.data);
        setLoading(false);

        // AI analysis runs separately (slower)
        try {
          const aiRes = await api.get(`/stocks/${ticker}/analysis`);
          setAnalysis(aiRes.data);
        } catch {
          toast.error("AI analysis unavailable");
        } finally {
          setAiLoading(false);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load stock");
        setLoading(false);
        setAiLoading(false);
      }
    };
    loadAll();
  }, [ticker]);

  const addToWatchlist = async () => {
    try {
      await api.post("/watchlist", { ticker });
      toast.success(`${ticker} added to watchlist`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-white border border-line rounded-sm w-1/3" />
            <div className="h-96 bg-white border border-line rounded-sm" />
          </div>
        </div>
      </div>
    );
  }

  const s = analysis?.snapshot;
  const ai = analysis?.ai;
  if (!s) return null;
  const cur = s.currency || "USD";
  const sym = symbolFor(cur);
  const mkt = marketFromTicker(s.ticker);

  const dayChangeColor =
    s.day_change_percent > 0 ? "text-moss-light" : s.day_change_percent < 0 ? "text-terracotta" : "text-stone_brand";

  return (
    <div className="min-h-screen bg-bone-50" data-testid="stock-detail-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Back + Add */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-stone_brand hover:text-stone_brand-dark transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setDigestOpen(true)}
              className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 gap-1.5"
              data-testid="open-digest-btn"
            >
              <Sparkles className="w-4 h-4" /> AI Research Digest
            </Button>
            <Button
              variant="outline"
              onClick={addToWatchlist}
              className="rounded-sm border-moss text-moss hover:bg-moss-bg"
              data-testid="add-watchlist-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Watchlist
            </Button>
          </div>
        </div>

        {/* Header */}
        <header className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div>
            <div className="overline mb-2">
              {mkt} · {s.exchange || "—"} · {s.sector || "—"} · {s.industry || "—"}
            </div>
            <div className="flex items-baseline gap-4">
              <h1 className="font-mono text-5xl text-stone_brand-dark font-semibold tracking-tight">
                {displayTicker(s.ticker)}
              </h1>
              <span
                className={`px-3 py-1 rounded-sm text-xs font-mono font-medium border ${verdictStyle(s.verdict)}`}
                data-testid="verdict-badge"
              >
                {s.verdict}
              </span>
            </div>
            <div className="font-serif-display text-2xl text-stone_brand mt-1 italic">
              {s.company_name}
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-4xl text-stone_brand-dark" data-testid="current-price">
              {fmtMoney(s.current_price, cur)}
            </div>
            <div className={`font-mono text-sm ${dayChangeColor}`}>
              {s.day_change >= 0 ? "+" : ""}{fmtMoney(s.day_change, cur)} ({fmtPct(s.day_change_percent)})
            </div>
          </div>
        </header>

        {/* Fair Value Card — side by side */}
        <section
          className="bg-white border border-line rounded-sm p-6 lg:p-8 mb-6"
          data-testid="fair-value-card"
        >
          <div className="flex items-center gap-2 mb-6">
            <Scale className="w-4 h-4 text-moss" />
            <div className="overline">— Fair Value Analysis</div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
            {/* Analyst side */}
            <div className="border-l-2 border-line pl-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif-display text-lg text-stone_brand-dark">
                  Analyst Consensus
                </h3>
                <span className="overline text-xs">
                  {s.analyst_count || 0} analysts
                </span>
              </div>
              <div className="font-mono text-4xl text-stone_brand-dark mb-1">
                {fmtMoney(s.analyst_target_mean, cur)}
              </div>
              <div className="text-xs text-stone_brand font-mono mb-4">
                Range: {fmtMoney(s.analyst_target_low, cur)} — {fmtMoney(s.analyst_target_high, cur)}
              </div>
              <div className="bg-bone-100 border border-line p-3 rounded-sm">
                <div className="overline mb-1">Upside vs Current</div>
                <div className={`font-mono text-xl ${marginColor(s.margin_of_safety_percent)}`}>
                  {fmtPct(s.margin_of_safety_percent)}
                </div>
              </div>
            </div>

            {/* AI side */}
            <div className="border-l-2 border-moss pl-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif-display text-lg text-stone_brand-dark flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-moss" /> AI Fair Value
                </h3>
                {ai?.confidence && (
                  <span className="overline text-xs">{ai.confidence} conf</span>
                )}
              </div>
              {aiLoading ? (
                <div className="space-y-2">
                  <div className="h-10 bg-bone-100 rounded-sm animate-pulse" />
                  <div className="h-4 bg-bone-100 rounded-sm animate-pulse w-2/3" />
                  <div className="h-14 bg-bone-100 rounded-sm animate-pulse mt-4" />
                </div>
              ) : ai ? (
                <>
                  <div className="font-mono text-4xl text-moss mb-1" data-testid="ai-fair-value">
                    {ai.ai_fair_value ? fmtMoney(ai.ai_fair_value, cur) : "—"}
                  </div>
                  <div className="text-xs text-stone_brand font-mono mb-4">
                    Independent DCF-style estimate · Claude Sonnet 4.5
                  </div>
                  <div className="bg-moss-bg border border-moss-light/30 p-3 rounded-sm">
                    <div className="overline mb-1">Upside vs Current</div>
                    <div className={`font-mono text-xl ${marginColor(ai.upside_percent)}`}>
                      {fmtPct(ai.upside_percent)}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-stone_brand italic">AI analysis unavailable</div>
              )}
            </div>
          </div>
        </section>

        {/* Your Targets — Morningstar FV + 2030 long-term target (only if user data exists) */}
        {(s.user_morningstar_fv || s.user_target_2030) && (
          <section className="bg-white border border-line rounded-sm p-6 lg:p-8 mb-6" data-testid="user-targets-card">
            <div className="flex items-center gap-2 mb-6">
              <Target className="w-4 h-4 text-moss" />
              <div className="overline">— Your Targets · From Your Worksheet</div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
              {s.user_morningstar_fv && (
                <div className="border-l-2 border-line pl-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-serif-display text-lg text-stone_brand-dark">
                      Morningstar Fair Value
                    </h3>
                    <span className="overline text-xs">Your input</span>
                  </div>
                  <div className="font-mono text-4xl text-stone_brand-dark mb-4">
                    {fmtMoney(s.user_morningstar_fv, cur)}
                  </div>
                  <div className="bg-bone-100 border border-line p-3 rounded-sm">
                    <div className="overline mb-1">Margin of Safety</div>
                    <div className={`font-mono text-xl ${marginColor(s.morningstar_margin_percent)}`}>
                      {fmtPct(s.morningstar_margin_percent)}
                    </div>
                  </div>
                </div>
              )}

              {s.user_target_2030 && (
                <div className="border-l-2 border-moss pl-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-serif-display text-lg text-stone_brand-dark">
                      2030 Target
                    </h3>
                    {s.user_multiplier_2030 && (
                      <span className="overline text-xs">{s.user_multiplier_2030}</span>
                    )}
                  </div>
                  <div className="font-mono text-4xl text-moss mb-4" data-testid="user-target-2030">
                    {fmtMoney(s.user_target_2030, cur)}
                  </div>
                  <div className="bg-moss-bg border border-moss-light/30 p-3 rounded-sm">
                    <div className="overline mb-1">5-Year Upside</div>
                    <div className={`font-mono text-xl ${marginColor(s.upside_2030_percent)}`}>
                      {fmtPct(s.upside_2030_percent)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* External Research — deep-link to 9 Indian / US platforms */}
        <section className="bg-white border border-line rounded-sm p-6 lg:p-8 mb-6" data-testid="external-research-panel">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-moss" />
              <div className="overline">
                — External Research · {mkt === "US" ? "Global Sources" : "Indian Sources"}
              </div>
            </div>
            <div className="text-xs font-mono text-stone_brand-light tracking-wider">
              OPENS IN NEW TAB
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {externalResearchLinks(s.ticker).map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center justify-between gap-2 border rounded-sm px-3.5 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5 hover:shadow-[0_2px_0_0_rgba(45,76,59,0.2)] ${link.color}`}
                data-testid={`external-link-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <span className="truncate">{link.name}</span>
                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
              </a>
            ))}
          </div>
          <p className="text-[0.7rem] font-mono text-stone_brand-light tracking-wider mt-4 uppercase">
            — Fairvest aggregates public data; these links open each platform's own research page for {displayTicker(s.ticker)}.
          </p>
        </section>

        {/* Chart */}
        <section className="bg-white border border-line rounded-sm p-6 lg:p-8 mb-6" data-testid="price-chart-card">
          <div className="overline mb-4">— Price · 1 Year</div>
          {history.length > 0 ? (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <ReLine data={history} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="#E4E4DC" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#8D9C96", fontFamily: "JetBrains Mono" }}
                    tickFormatter={(v) => v.slice(5)}
                    interval={Math.floor(history.length / 8)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#8D9C96", fontFamily: "JetBrains Mono" }}
                    domain={["dataMin", "dataMax"]}
                    tickFormatter={(v) => `${sym}${Number(v).toFixed(0)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#FFFFFF",
                      border: "1px solid #E4E4DC",
                      borderRadius: 2,
                      fontSize: 12,
                      fontFamily: "JetBrains Mono",
                    }}
                    formatter={(v) => [`${sym}${Number(v).toFixed(2)}`, "Close"]}
                  />
                  <Line
                    type="monotone"
                    dataKey="close"
                    stroke="#2D4C3B"
                    strokeWidth={2}
                    dot={false}
                  />
                </ReLine>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-stone_brand italic">No chart data available</div>
          )}
        </section>

        {/* AI Thesis + Bull/Bear */}
        <section className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* Thesis */}
          <div className="lg:col-span-2 relative bg-white border border-line rounded-sm p-6 lg:p-8 overflow-hidden" data-testid="ai-thesis-card">
            <div className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1762279389020-eeeb69c25813?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80')",
                backgroundSize: "cover",
              }}
            />
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-4 h-4 text-moss" />
                <div className="overline">— AI Investment Thesis</div>
              </div>
              {aiLoading ? (
                <div className="space-y-2">
                  <div className="h-4 bg-bone-100 rounded-sm animate-pulse" />
                  <div className="h-4 bg-bone-100 rounded-sm animate-pulse" />
                  <div className="h-4 bg-bone-100 rounded-sm animate-pulse w-3/4" />
                </div>
              ) : ai?.thesis ? (
                <>
                  <p className="font-serif-display text-lg lg:text-xl text-stone_brand-dark italic leading-relaxed mb-5">
                    "{ai.thesis}"
                  </p>
                  {ai.industry_outlook && (
                    <div className="border-t border-line pt-4">
                      <div className="overline mb-2">— Industry Outlook</div>
                      <p className="text-sm text-stone_brand leading-relaxed">
                        {ai.industry_outlook}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-stone_brand italic">Thesis unavailable.</p>
              )}
            </div>
          </div>

          {/* Bull / Bear */}
          <div className="space-y-6">
            <div className="bg-white border border-line rounded-sm p-6" data-testid="bull-case-card">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-moss-light" />
                <div className="overline">— Bull Case</div>
              </div>
              {aiLoading ? (
                <div className="space-y-2">
                  <div className="h-4 bg-bone-100 rounded-sm animate-pulse" />
                  <div className="h-4 bg-bone-100 rounded-sm animate-pulse" />
                </div>
              ) : (
                <ul className="space-y-2.5">
                  {(ai?.bull_case || []).map((b, i) => (
                    <li key={i} className="flex gap-2 text-sm text-stone_brand-dark">
                      <ChevronRight className="w-4 h-4 text-moss-light flex-shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white border border-line rounded-sm p-6" data-testid="bear-case-card">
              <div className="flex items-center gap-2 mb-4">
                <TrendingDown className="w-4 h-4 text-terracotta" />
                <div className="overline">— Bear Case</div>
              </div>
              {aiLoading ? (
                <div className="space-y-2">
                  <div className="h-4 bg-bone-100 rounded-sm animate-pulse" />
                  <div className="h-4 bg-bone-100 rounded-sm animate-pulse" />
                </div>
              ) : (
                <ul className="space-y-2.5">
                  {(ai?.bear_case || []).map((b, i) => (
                    <li key={i} className="flex gap-2 text-sm text-stone_brand-dark">
                      <ChevronRight className="w-4 h-4 text-terracotta flex-shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* Broker Consensus — Yahoo + Moneycontrol trajectory */}
        <BrokerCallsPanel ticker={ticker} />

        {/* Fundamentals */}
        <section className="bg-white border border-line rounded-sm p-6 lg:p-8" data-testid="fundamentals-panel">
          <div className="overline mb-5">— Fundamentals</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5">
            <Metric label="Market Cap" value={fmtLarge(s.market_cap, cur)} />
            <Metric label="Trailing P/E" value={fmtRatio(s.pe_ratio)} />
            <Metric label="Forward P/E" value={fmtRatio(s.forward_pe)} />
            <Metric label="PEG Ratio" value={fmtRatio(s.peg_ratio)} />
            <Metric label="Price/Book" value={fmtRatio(s.price_to_book)} />
            <Metric label="Price/Sales" value={fmtRatio(s.price_to_sales)} />
            <Metric label="EPS (TTM)" value={fmtMoney(s.eps, cur)} />
            <Metric label="Forward EPS" value={fmtMoney(s.forward_eps, cur)} />
            <Metric label="Debt / Equity" value={fmtRatio(s.debt_to_equity)} />
            <Metric
              label="ROE"
              value={s.return_on_equity !== null ? fmtPct(s.return_on_equity * 100) : "—"}
            />
            <Metric
              label="Profit Margin"
              value={s.profit_margin !== null ? fmtPct(s.profit_margin * 100) : "—"}
            />
            <Metric
              label="Revenue Growth"
              value={s.revenue_growth !== null ? fmtPct(s.revenue_growth * 100) : "—"}
            />
            <Metric label="52W High" value={fmtMoney(s.fifty_two_week_high, cur)} />
            <Metric label="52W Low" value={fmtMoney(s.fifty_two_week_low, cur)} />
            <Metric label="50D Avg" value={fmtMoney(s.fifty_day_avg, cur)} />
            <Metric label="200D Avg" value={fmtMoney(s.two_hundred_day_avg, cur)} />
          </div>
        </section>
      </main>
      {digestOpen && (
        <DigestModal
          ticker={s.ticker}
          companyName={s.company_name}
          onClose={() => setDigestOpen(false)}
        />
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="overline mb-1.5">{label}</div>
      <div className="font-mono text-base text-stone_brand-dark">{value}</div>
    </div>
  );
}
